<!-- src/components/DocumentPreview.vue -->
<template>
  <div class="box preview" :class="variant === 'floating' ? 'is-floating' : ''">
    <button
      v-if="closeable"
      type="button"
      class="delete is-small close-btn"
      @click="$emit('close')"
    ></button>

    <h2 class="title is-6">{{ title }}</h2>

    <div v-for="field in resolvedFields" :key="field.key" class="mb-2">
      <strong>{{ getLabel(field.key) }} :</strong>
      <span>{{ getValue(field.key) }}</span>
    </div>

    <slot name="footer"></slot>
  </div>
</template>

<script>
/*
  Highly configurable document preview:
  - fields: array of { key, visible } or simple string keys
  - labels: optional per-field custom labels
  - formatters: optional per-field function(doc) => string
  - variant: 'inline' | 'floating'
*/
export default {
  name: 'DocumentPreview',
  props: {
    document: { type: Object, required: true },
    fields: {
      type: Array,
      default: () => [
        // default field order (override in parent)
        'number',
        'third_party_name',
        'gross_amount',
        'get_due_date_formatted',
        'status',
      ],
    },
    labels: {
      type: Object,
      default: () => ({
        number: 'Numéro',
        third_party_name: 'Tiers',
        gross_amount: 'Montant',
        get_due_date_formatted: 'Échéance',
        status: 'Statut',
      }),
    },
    formatters: {
      // { key: (doc) => string }
      type: Object,
      default: () => ({}),
    },
    getStatusLabel: { type: Function, default: null }, // optional function from parent
    title: { type: String, default: 'Aperçu' },
    closeable: { type: Boolean, default: true },
    variant: { type: String, default: 'inline' }, // 'inline' | 'floating'
  },
  computed: {
    resolvedFields() {
      // Normalize fields to array of { key, visible: true }
      return this.fields
        .map((f) => (typeof f === 'string' ? { key: f, visible: true } : f))
        .filter((f) => f.visible !== false);
    },
  },
  methods: {
    getLabel(key) {
      return this.labels?.[key] ?? key;
    },
    getValue(key) {
      // custom formatter takes precedence
      const fmt = this.formatters?.[key];
      if (typeof fmt === 'function') return fmt(this.document);

      // special case: status via parent helper
      if (key === 'status' && this.getStatusLabel) {
        return this.getStatusLabel(this.document);
      }

      // fallback: raw value (stringify safely)
      const val = this.document?.[key];
      if (val === null || val === undefined) return '—';
      return String(val);
    },
  },
};
</script>

<style scoped>
.preview.is-floating {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  width: 320px;
  max-height: 40vh;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}
.close-btn {
  float: right;
  margin-top: -0.5rem;
  margin-right: -0.5rem;
}
</style>
